import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
    Button,
    Divider,
    DrawerBody,
    DrawerFooter,
    DrawerHeader,
    DrawerHeaderTitle,
    Dropdown,
    Field,
    Input,
    Option,
    OverlayDrawer,
    Spinner,
    Subtitle2,
    Textarea,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';

// component functions
import * as ComponentFunctions from './PractitionerConsumption.Form.Functions';

// styles
import './PractitionerConsumption.Form.Styles.css';

// sub-components
import { DateTimeInput } from '../../../../commons/component/Inputs/DateTimeInput/DateTimeInput';
import { CurrencySelectorComponent } from '../../../../commons/component/Inputs/CurrencySelector/CurrencySelectorComponent';
import ContractConceptSelectorComponent from '../../../../commons/component/Inputs/ContractConceptSelector/ContractConceptSelector.Component';
import ContractStagesSelectorComponent from '../../../../commons/component/Inputs/ContractStagesSelector/ContractStages.Selector.Component';
import ProtocolPatientSelectorComponent from '../../../../commons/component/Inputs/ProtocolPatientSelector/ProtocolPatientSelector.Component';
import { ErrorMessageComponent } from '../../../Error/ErrorMessageComponent';

// models
import { ContractConcept } from '../../../../models/Entities/Contracts/ContractConcept';
import { ContractStage } from '../../../../models/Entities/Contracts/ContractStage';
import { ProtocolPatient } from '../../../../models/Entities/Protocols/ProtocolPatient';
import { Protocol } from '../../../../models/Entities/Protocols/Protocol';
import { NewPractitionerConsumption } from '../../../../models/Entities/Practitioners/NewPractitionerConsumption';
import { ProtocolConsumptionRealizeTypeEnum } from '../../../../models/Enums/ProtocolConsumptionRealizeTypeEnum';
import { PatientVisitStatusEnum } from '../../../../models/Enums/PatientVisitStatusEnum';

const REALIZE_TYPE_OPTIONS = [
    { key: ProtocolConsumptionRealizeTypeEnum.RealizedFaceToFace, label: 'Presencial' },
    { key: ProtocolConsumptionRealizeTypeEnum.RealizedRemotely, label: 'Remota' },
    { key: ProtocolConsumptionRealizeTypeEnum.RealizedAtHome, label: 'Domicilio' },
    { key: ProtocolConsumptionRealizeTypeEnum.NotRealized, label: 'No realizada' },
];

const VISIT_STATUS_OPTIONS = [
    { key: PatientVisitStatusEnum.Completed, label: 'Completada' },
    { key: PatientVisitStatusEnum.Started, label: 'Iniciada' },
    { key: PatientVisitStatusEnum.Skipped, label: 'Salteada' },
    { key: PatientVisitStatusEnum.Failed, label: 'Falla de selección' },
    { key: PatientVisitStatusEnum.Changed, label: 'Modificada' },
    { key: PatientVisitStatusEnum.AutoCompleted, label: 'Autocompletada' },
];

const parseDecimal = (value: string): number => {
    const parsed = Number.parseFloat(value.replace(',', '.'));
    return Number.isNaN(parsed) ? 0 : parsed;
};

// keeps what is typed as text (so "1," or an empty field are valid intermediate states) while
// refusing anything that is not a decimal number
const sanitizeDecimalText = (value: string): string => {
    const cleaned = value.replace(/[^0-9.,]/g, '');
    const separatorIndex = cleaned.search(/[.,]/);

    if (separatorIndex < 0) {
        return cleaned;
    }

    // only the first separator survives
    return cleaned.slice(0, separatorIndex + 1) + cleaned.slice(separatorIndex + 1).replace(/[.,]/g, '');
};

const PractitionerConsumptionFormComponent = (props: ComponentFunctions.Props) => {

    // ── form state ──────────────────────────────────────────────────────────
    const [submitted, setSubmitted] = useState(false);
    const [wasSaving, setWasSaving] = useState(false);

    const [protocol, setProtocol] = useState<Protocol | undefined>();
    const [patient, setPatient] = useState<ProtocolPatient | undefined>();
    const [stage, setStage] = useState<ContractStage | undefined>();
    const [concept, setConcept] = useState<ContractConcept | undefined>();
    const [serviceName, setServiceName] = useState('');
    const [serviceDueDate, setServiceDueDate] = useState<Date | undefined>(undefined);
    const [realizedType, setRealizedType] = useState<number>(ProtocolConsumptionRealizeTypeEnum.RealizedFaceToFace);
    const [visitStatus, setVisitStatus] = useState<number>(PatientVisitStatusEnum.Completed);
    const [currencyCode, setCurrencyCode] = useState('');
    const [quantityText, setQuantityText] = useState('1');
    const [unitAmountText, setUnitAmountText] = useState('0');
    const [comments, setComments] = useState('');

    // ─────────── DIAGNÓSTICO TEMPORAL (quitar cuando se resuelva) ───────────
    const pcfRenderCountRef = React.useRef(0);
    pcfRenderCountRef.current += 1;
    console.log(`[PCF] render #${pcfRenderCountRef.current} cantidad="${quantityText}" valorUnitario="${unitAmountText}"`);

    useEffect(() => {
        console.log('%c[PCF] MONTADO', 'color:#0a0');

        return () => console.log('%c[PCF] DESMONTADO', 'color:#a00');
    }, []);
    // ────────────────────────────────────────────────────────────────────────

    const quantity = parseDecimal(quantityText);
    const unitAmount = parseDecimal(unitAmountText);
    const amount = quantity * unitAmount;

    const contractId = protocol?.contractAssociation?.contractId;

    const resetForm = () => {
        console.log('%c[PCF] resetForm()', 'color:#c60');
        setSubmitted(false);
        setProtocol(undefined);
        setPatient(undefined);
        setStage(undefined);
        setConcept(undefined);
        setServiceName('');
        setServiceDueDate(undefined);
        setRealizedType(ProtocolConsumptionRealizeTypeEnum.RealizedFaceToFace);
        setVisitStatus(PatientVisitStatusEnum.Completed);
        setCurrencyCode('');
        setQuantityText('1');
        setUnitAmountText('0');
        setComments('');
    };

    // Start from a clean form on every open
    useEffect(() => {
        if (props.isOpen) {
            resetForm();
        }
    }, [props.isOpen]);

    // The practitioner's protocols carry no contract association; fetch the full
    // protocol so the concept and stage selectors get a contract to work with.
    useEffect(() => {
        if (protocol?.id && protocol.contractAssociation === undefined) {
            props.GetAllProtocolContractAssociations(protocol.id);
        }
    }, [protocol?.id]);

    // Close once the save completes (isSaving true → false without failure)
    useEffect(() => {
        if (wasSaving && !props.isSaving && !props.failSaving) {
            if (props.onSaved) props.onSaved();
            props.onClose();
        }
        setWasSaving(props.isSaving);
    }, [props.isSaving]);

    // ── validation ──────────────────────────────────────────────────────────
    const errors = {
        protocol: !protocol ? 'Seleccione un protocolo' : undefined,
        service: !concept && !serviceName.trim() ? 'Indique un concepto o el nombre del servicio' : undefined,
        currency: !currencyCode ? 'Seleccione una moneda' : undefined,
        quantity: quantity <= 0 ? 'La cantidad debe ser mayor a cero' : undefined,
        dueDate: !serviceDueDate ? 'Indique la fecha del servicio' : undefined,
    };

    const isValid = Object.values(errors).every((error) => error === undefined);

    const onSubmit = () => {
        setSubmitted(true);
        if (!isValid || !protocol) return;

        const consumption: NewPractitionerConsumption = {
            // identity
            practitionerId: props.PractitionerId,
            protocolId: protocol.id,
            protocolName: protocol.title || '',
            contractId: contractId ?? 0,
            addendumId: 0,
            contractConceptId: concept?.id ?? 0,
            stageId: stage?.id ?? 0,
            settlementId: 0,
            instanceId: '',
            parentInstanceId: '',
            emrCrossVersionId: 0,

            // practitioner
            practitionerName: props.Practitioner?.fullName || '',
            currentPractitionerId: props.PractitionerId,
            currentPractitionerName: props.Practitioner?.fullName || '',

            // protocol / patient
            patientNumberInProtocol: patient?.numberInProtocol || '',
            stageName: stage?.stageName || '',
            stageInternalCrossVersionId: '',

            // service
            serviceName: serviceName.trim() || concept?.concept?.conceptName || '',
            serviceDueDate: (serviceDueDate as Date).toISOString(),
            conceptDescription: concept?.concept?.conceptName || serviceName.trim(),
            settlementIdentifier: '',
            settlementName: '',

            // amounts
            quantity: quantity,
            coefficientApplied: 0,
            currencyCode: currencyCode,
            unitAmount: unitAmount,
            amount: amount,
            payeeAmount: 0,
            pendingAmount: amount,
            referenceUnitAmount: unitAmount,
            percentageApplied: 0,

            // classification — a manually loaded consumption starts unsettled
            status: 0,
            typeOfService: 0,
            realizedType: realizedType,
            originalConsumptionType: 0,
            currentConsumptionType: 0,
            origin: 1,
            visitStatus: visitStatus,

            // flags
            markedToSettle: false,
            markedAsPayee: false,
            markedAsFlagged: false,
            markedAsItemized: false,
            markedAsRefundable: false,
            markedAsNeedMoreInfo: false,
            markedAsHaveWarnings: false,
            markedAsTrouble: false,

            // notes
            comments: comments,
            moreInfoComments: '',
            warningsComments: '',
            troubleComments: '',

            // tags
            tag1: '',
            tag2: '',
            tag3: '',
            tag4: '',
        };

        props.CreatePractitionerConsumption(consumption);
    };

    return (
        <OverlayDrawer
            open={props.isOpen}
            position="end"
            size="medium"
            onOpenChange={(_, data) => { if (!data.open) props.onClose(); }}
            className="pcf-drawer"
        >
            <DrawerHeader>
                <DrawerHeaderTitle
                    action={
                        <Button
                            appearance="subtle"
                            aria-label="Cerrar"
                            icon={<Dismiss24Regular />}
                            onClick={props.onClose}
                        />
                    }
                >
                    Agregar consumo
                </DrawerHeaderTitle>
                <span className="pcf-subtitle">
                    {props.Practitioner?.fullName || 'Profesional'}
                </span>
            </DrawerHeader>

            <DrawerBody className="pcf-body">
                {props.failSaving && props.errorOnSaving && (
                    <ErrorMessageComponent Error={props.errorOnSaving} />
                )}

                <Subtitle2 className="pcf-section">Protocolo</Subtitle2>

                <Field
                    label="Protocolo"
                    required
                    validationState={submitted && errors.protocol ? 'error' : 'none'}
                    validationMessage={submitted ? errors.protocol : undefined}
                >
                    <Dropdown
                        placeholder="Seleccione un protocolo"
                        value={protocol?.title || ''}
                        selectedOptions={protocol ? [String(protocol.id)] : []}
                        onOptionSelect={(_, data) => {
                            const selected = props.Protocols.find((item) => String(item.id) === data.optionValue);
                            setProtocol(selected);
                            // dependent selections belong to the previous protocol
                            setPatient(undefined);
                            setStage(undefined);
                            setConcept(undefined);
                        }}
                    >
                        {props.Protocols.map((item) => (
                            <Option key={item.id} value={String(item.id)} text={item.title || `#${item.id}`}>
                                {item.title || `#${item.id}`}
                            </Option>
                        ))}
                    </Dropdown>
                </Field>

                {protocol && (
                    <ProtocolPatientSelectorComponent
                        ProtocolId={protocol.id}
                        MultiselectEnable={false}
                        ProtocolPatientsSelectedNumber={patient ? [patient.numberInProtocol] : []}
                        onSelectedProtocolPatient={(patients: ProtocolPatient[]) => setPatient(patients[0])}
                    />
                )}

                {protocol && contractId === undefined && (
                    <div className="pcf-note">
                        Este protocolo no tiene un contrato asociado: no se pueden ofrecer conceptos ni visitas.
                        Se puede cargar el consumo indicando el nombre del servicio.
                    </div>
                )}

                {contractId !== undefined && (
                    <>
                        <ContractStagesSelectorComponent
                            ContractId={contractId}
                            MultiselectEnable={false}
                            ContractStagesSelected={stage ? [stage.id] : []}
                            onSelectedContractStage={(stages: ContractStage[]) => setStage(stages[0])}
                        />

                        <ContractConceptSelectorComponent
                            ContractId={contractId}
                            referralDate={serviceDueDate}
                            MultiselectEnable={false}
                            ContractConceptsSelected={concept ? [concept.id] : []}
                            onSelectedContractConcept={(concepts: ContractConcept[]) => setConcept(concepts[0])}
                        />
                    </>
                )}

                <Divider className="pcf-divider" />
                <Subtitle2 className="pcf-section">Servicio</Subtitle2>

                <Field
                    label="Nombre del servicio"
                    validationState={submitted && errors.service ? 'error' : 'none'}
                    validationMessage={submitted ? errors.service : undefined}
                    hint={concept ? 'Opcional: si se deja vacío se usa el nombre del concepto' : undefined}
                >
                    <Input
                        value={serviceName}
                        placeholder={concept?.concept?.conceptName || 'Descripción del servicio prestado'}
                        onChange={(_, data) => setServiceName(data.value)}
                    />
                </Field>

                <DateTimeInput
                    label="Fecha del servicio"
                    placeHolder="Seleccione la fecha"
                    selectedDate={serviceDueDate}
                    isSubmitted={submitted}
                    isRequired
                    errorMessage={submitted ? errors.dueDate : undefined}
                    onSelectDate={(date: Date | undefined) => setServiceDueDate(date)}
                />

                <div className="pcf-row">
                    <Field label="Modalidad">
                        <Dropdown
                            value={REALIZE_TYPE_OPTIONS.find((option) => option.key === realizedType)?.label || ''}
                            selectedOptions={[String(realizedType)]}
                            onOptionSelect={(_, data) => setRealizedType(Number(data.optionValue))}
                        >
                            {REALIZE_TYPE_OPTIONS.map((option) => (
                                <Option key={option.key} value={String(option.key)} text={option.label}>
                                    {option.label}
                                </Option>
                            ))}
                        </Dropdown>
                    </Field>

                    <Field label="Estado de la visita">
                        <Dropdown
                            value={VISIT_STATUS_OPTIONS.find((option) => option.key === visitStatus)?.label || ''}
                            selectedOptions={[String(visitStatus)]}
                            onOptionSelect={(_, data) => setVisitStatus(Number(data.optionValue))}
                        >
                            {VISIT_STATUS_OPTIONS.map((option) => (
                                <Option key={option.key} value={String(option.key)} text={option.label}>
                                    {option.label}
                                </Option>
                            ))}
                        </Dropdown>
                    </Field>
                </div>

                <Divider className="pcf-divider" />
                <Subtitle2 className="pcf-section">Importe</Subtitle2>

                <CurrencySelectorComponent
                    label="Moneda"
                    isRequired
                    defaultSelected={currencyCode}
                    errorMessage={submitted ? errors.currency : undefined}
                    onCurrencyChange={(currency: string) => setCurrencyCode(currency)}
                />

                <div className="pcf-row">
                    <Field
                        label="Cantidad"
                        required
                        validationState={submitted && errors.quantity ? 'error' : 'none'}
                        validationMessage={submitted ? errors.quantity : undefined}
                    >
                        <Input
                            type="text"
                            inputMode="decimal"
                            value={quantityText}
                            onChange={(_, data) => {
                                console.log('[PCF] onChange cantidad:', JSON.stringify(data.value), '->', JSON.stringify(sanitizeDecimalText(data.value)));
                                setQuantityText(sanitizeDecimalText(data.value));
                            }}
                        />
                    </Field>

                    <Field label="Valor unitario">
                        <Input
                            type="text"
                            inputMode="decimal"
                            contentAfter={currencyCode || undefined}
                            value={unitAmountText}
                            onChange={(_, data) => {
                                console.log('[PCF] onChange valor:', JSON.stringify(data.value), '->', JSON.stringify(sanitizeDecimalText(data.value)));
                                setUnitAmountText(sanitizeDecimalText(data.value));
                            }}
                        />
                    </Field>
                </div>

                <div className="pcf-total">
                    <span className="pcf-total__label">Total</span>
                    <span className="pcf-total__value">
                        {amount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        {currencyCode ? ` ${currencyCode}` : ''}
                    </span>
                </div>

                <Divider className="pcf-divider" />

                <Field label="Comentarios">
                    <Textarea
                        value={comments}
                        resize="vertical"
                        placeholder="Notas sobre este consumo (opcional)"
                        onChange={(_, data) => setComments(data.value)}
                    />
                </Field>
            </DrawerBody>

            <DrawerFooter className="pcf-footer">
                <Button appearance="secondary" onClick={props.onClose} disabled={props.isSaving}>
                    Cancelar
                </Button>
                <Button
                    appearance="primary"
                    onClick={onSubmit}
                    disabled={props.isSaving || (submitted && !isValid)}
                    icon={props.isSaving ? <Spinner size="extra-tiny" /> : undefined}
                >
                    Agregar consumo
                </Button>
            </DrawerFooter>
        </OverlayDrawer>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(PractitionerConsumptionFormComponent as any);
